import React, { useState } from "react";
import {
  addSeperatorToInput,
  checkForDecimalNumbers,
  getNegativeNumber,
  getNegativeWithDecimalsNumber,
  getPan,
  getPercent,
  getUnboundedPercent,
  getPrice,
  getPriceWithDecimals,
} from "../helper";
import { Input } from "antd";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface TextInputProps {
  name: string;
  value?: string;
  label: string;
  onChange: (name: string, value: any) => void;
  classname?: string;
  type?: "text" | "password";
  direction?: "ltr" | "rtl";
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  kind?:
    | "price"
    | "number"
    | "percent"
    | "pan"
    | "priceWithDecimal"
    | "negativeNumbers"
    | "negativeDecimalNumbers";
  disabled?: boolean;
  readOnly?: boolean;
  endIcon?: JSX.Element;
  autoComplete?: boolean | string;
  extraLabel?: boolean;
  extraText?: any;
  allowPercentAbove100?: boolean;
}

export type Ref = HTMLButtonElement;

const TextInput = ({
  name,
  value = "",
  onChange,
  label,
  classname,
  type = "text",
  direction,
  onFocus,
  onBlur,
  kind,
  endIcon,
  autoFocus = false,
  disabled = false,
  readOnly = false,
  autoComplete = true,
  extraLabel = false,
  extraText,
  allowPercentAbove100 = false,
}: TextInputProps) => {
  const changeHandler = (newValue: string) => {
    if (kind === "price") {
      const pr = getPrice(newValue);
      onChange(name, pr);
    } else if (kind === "priceWithDecimal")
      onChange(name, checkForDecimalNumbers(newValue));
    else if (kind === "number") {
      const num = getPrice(newValue);
      onChange(name, num);
    } else if (kind === "pan") {
      const pn = getPan(newValue);
      onChange(name, pn);
    } else if (kind === "percent") {
      const pn = allowPercentAbove100
        ? getUnboundedPercent(newValue)
        : getPercent(newValue);
      onChange(name, pn);
    } else if (kind === "negativeNumbers") {
      const v = getNegativeNumber(newValue);
      onChange(name, v);
    } else if (kind === "negativeDecimalNumbers") {
      const v = getNegativeWithDecimalsNumber(newValue);
      onChange(name, v);
    } else onChange(name, newValue);
  };

  const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (kind === "percent") {
      const float = parseFloat(value);
      onChange(name, float || float === 0 ? float.toString() : "");
    }
    if (onBlur) onBlur(e);
  };

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [inputType, setInputType] = useState(type);

  const getSuffix = () => {
    if (endIcon) return endIcon;
    if (type !== "password") return undefined;
    if (isPasswordVisible)
      return (
        <div
          className="!text-gray-600"
          onClick={() => {
            setIsPasswordVisible(false);
            setInputType("password");
          }}
        >
          <VisibilityOffIcon fontSize="small" />
        </div>
      );
    return (
      <div
        className="!text-gray-600"
        onClick={() => {
          setIsPasswordVisible(true);
          setInputType("text");
        }}
      >
        <VisibilityIcon fontSize="small" />
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col justify-start items-start gap-1 relative">
      {extraLabel ? (
        <div className="text-[10px] whitespace-nowrap w-auto">{label}</div>
      ) : (
        <></>
      )}
      <Input
        dir={direction}
        autoComplete={
          typeof autoComplete === "string"
            ? autoComplete
            : autoComplete
              ? "on"
              : "off"
        }
        style={{ width: "100%" }}
        name={name}
        value={addSeperatorToInput(value, kind)}
        onChange={(e) => changeHandler(e.target.value)}
        id="outlined-basic"
        placeholder={label}
        readOnly={readOnly}
        className={
          kind === "negativeNumbers" ||
          kind === "number" ||
          kind === "percent" ||
          kind === "price" ||
          kind === "priceWithDecimal" ||
          kind === "negativeDecimalNumbers"
            ? "!ltr !text-right"
            : "" + classname
        }
        type={inputType}
        onFocus={onFocus}
        onBlur={blurHandler}
        autoFocus={autoFocus}
        suffix={getSuffix()}
        disabled={disabled}
      />
      {extraText || extraText === 0 ? (
        <div className="absolute top-7 left-2 text-xs text-gray-500">
          {extraText}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default TextInput;
